import { DayPilot } from 'daypilot-pro-react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import { ScheduleMenuItem } from 'Manager/Schedule/SchedulerContextMenu/helpers'
import { openModal } from 'store/actions/modals'
import { ACTIONS } from 'Manager/Schedule/SchedulerContextMenu/constants'

type Props = {
  clearSelection: (scheduler: DayPilot.MenuItemClickArgs['source']['calendar']) => void
  copySelection: (source: DayPilot.MenuItemClickArgs['source']) => void
}
export type MultiItems = { [key: string]: ScheduleMenuItem }

const useMultiItems = ({ clearSelection, copySelection }: Props): MultiItems => {
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const mainItems: MultiItems = {
    [ACTIONS.EDIT]: {
      text: t('manager.schedule.CONTEXT_MENU.EDIT_SELECTED'),
      order: 1,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleMultiEditModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id) }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.EDIT_USER]: {
      text: t('manager.schedule.CONTEXT_MENU.EDIT_SELECTED'),
      order: 1,
      onClick: ({ source: { calendar: scheduler } }) =>
        dispatch(openModal('ScheduleAssignEmployeeModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id) }))
    },
    [ACTIONS.PUBLISH]: {
      text: t('manager.schedule.CONTEXT_MENU.PUBLISH_SELECTED'),
      order: 2,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(
          openModal(
            'SchedulePublishModal',
            // Create payload with shiftIds and timeOffIds
            scheduler.multiselect.events().reduce(
              (acc, { data: { id, isTimeOff } }) => {
                if (isTimeOff) acc.timeOffIds.push(id)
                else acc.shiftIds.push(id)
                return acc
              },
              { shiftIds: [], timeOffIds: [] }
            )
          )
        )
      }
    },
    [ACTIONS.UNPUBLISH]: {
      text: t('manager.schedule.CONTEXT_MENU.UNPUBLISH_SELECTED'),
      order: 2,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(
          openModal(
            'ScheduleUnpublishModal',
            // Create payload with shiftIds and timeOffIds
            { shiftIds: scheduler.multiselect.events().map(({ data: { id } }) => id) }
          )
        )
      }
    },
    [ACTIONS.NOTIFY_TIMEOFF]: {
      text: t('manager.schedule.CONTEXT_MENU.SEND_TIMEOFF_NOTIFICATION'),
      order: 2,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(
          openModal(
            'SchedulePublishModal',
            // Create payload with shiftIds and timeOffIds
            scheduler.multiselect.events().reduce(
              (acc, { data: { id, isTimeOff } }) => {
                if (isTimeOff) acc.timeOffIds.push(id)
                return acc
              },
              { timeOffIds: [], isConfirming: true }
            )
          )
        )
      }
    },
    [ACTIONS.NOTIFY_SHIFT]: {
      text: t('manager.schedule.CONTEXT_MENU.SEND_SHIFT_NOTIFICATION'),
      order: 3,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(
          openModal(
            'SchedulePublishModal',
            // Create payload with shiftIds and timeOffIds
            scheduler.multiselect.events().reduce(
              (acc, { data: { id, isTimeOff } }) => {
                if (!isTimeOff) acc.shiftIds.push(id)
                return acc
              },
              { shiftIds: [], isConfirmingShift: true }
            )
          )
        )
      }
    },
    [ACTIONS.CANCEL]: {
      text: t('manager.schedule.CONTEXT_MENU.CANCEL_SELECTED'),
      order: 4,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleCancelModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id) }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.RESTORE]: {
      text: t('manager.schedule.CONTEXT_MENU.RESTORE_SELECTED'),
      order: 4,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleRestoreModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id) }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.COPY]: {
      text: t('manager.schedule.CONTEXT_MENU.COPY_SELECTION'),
      order: 5,
      onClick: ({ source }) => {
        copySelection(source)
      }
    }
  } as const

  const itemsInSubmenus: MultiItems = {
    [ACTIONS.DELETE]: {
      text: t('manager.schedule.CONTEXT_MENU.DELETE_SELECTED'),
      submenu: 'advanced',
      order: 1,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleDeleteModal', { events: scheduler.multiselect.events().map(e => e.data) }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.UNASSIGN_EMPLOYEE]: {
      text: t('manager.schedule.CONTEXT_MENU.UNASSIGN_EMPLOYEES'),
      submenu: 'advanced',
      order: 2,
      onClick: ({ source: { calendar: scheduler } }) =>
        dispatch(
          openModal('ScheduleUnassignEmployeeModal', {
            ids: scheduler.multiselect.events().map(({ data: { id } }) => id)
          })
        )
    },

    [ACTIONS.FINALIZE]: {
      text: t('manager.schedule.CONTEXT_MENU.FINALIZE_SELECTED'),
      submenu: 'advanced',
      order: 3,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleFinalizeModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id), isFinalizing: true }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.UNFINALIZE]: {
      text: t('manager.schedule.CONTEXT_MENU.UNFINALIZE_SELECTED'),
      submenu: 'advanced',
      order: 3,
      onClick: ({ source: { calendar: scheduler } }) => {
        dispatch(openModal('ScheduleFinalizeModal', { ids: scheduler.multiselect.events().map(({ data: { id } }) => id), isFinalizing: false }))
        clearSelection(scheduler)
      }
    },
    [ACTIONS.CREATE_LIST]: {
      text: t('manager.schedule.CONTEXT_MENU.CREATE_LIST_SELECTED'),
      submenu: 'advanced',
      order: 4,
      onClick: ({ source: { calendar: scheduler } }) => {
        // Extract events from multiselect
        const selected = scheduler.multiselect.events().map(({ data }) => data)

        dispatch(openModal('ContactListCreationModal', { ids: selected.map(({ id }) => id) }))
      }
    }
  } as const
  return { ...mainItems, ...itemsInSubmenus }
}

export { useMultiItems }
